import {
	Modal,
	H2,
	Text,
	Button
} from '@components';
import styles from './Dialog.module.scss';
import { BUTTON_SIZE, BUTTON_TYPES } from '@types';

interface ISecondaryAction {
	text: string;
	onClick: () => void;
	disabled?: boolean;
	loading?: boolean;
	type?: BUTTON_TYPES;
}

interface IPropsBase {
	open: boolean;
	onClose: (b: boolean) => void;
	onCancel?: () => void;
	title: string;
	text: string;
	actionButtonFn: () => void;
	actionButtonText: string;
	cancelButtonText?: string;
	closeOnButtonAction?: boolean;
	closeOnEscape?: boolean;
	closeOnClickOutside?: boolean;
	actionButtonDisabled?: boolean;
	actionButtonSize?: BUTTON_SIZE;
	actionButtonLoading?: boolean
	actionButtonActsAsCancel?: boolean;
	secondaryAction?: ISecondaryAction;
	footerDescription?: JSX.Element
	footerLeft?: JSX.Element;
	fullWidthFooter?: boolean;
}
interface ISmallProps extends IPropsBase {
	size?: 'small';
	slot?: JSX.Element;
}
interface IMidLargeProps extends IPropsBase {
	size?: 'mid' | 'large';
	slot?: JSX.Element;
}

type TProps = ISmallProps | IMidLargeProps;

export const Dialog = (props: TProps) => {
	const {
		open,
		onClose,
		onCancel,
		size = 'mid',
		title,
		text,
		closeOnEscape,
		actionButtonFn,
		actionButtonText,
		cancelButtonText = 'Cancel',
		closeOnClickOutside = true,
		closeOnButtonAction = true,
		actionButtonDisabled = false,
		actionButtonLoading = false,
		actionButtonActsAsCancel = false,
		secondaryAction,
		slot,
		fullWidthFooter = false,
		footerDescription = null,
		footerLeft = null,
		actionButtonSize = BUTTON_SIZE.LARGE,
	} = props;

	const getClassName = () => {
		switch (size) {
		case 'small':
			return styles.small;
		case 'large':
			return styles.large;
		default:
			return styles.mid;
		}
	}

	const triggerFn = async () => {
		return await new Promise ((resolve) => {
			resolve(actionButtonFn());
		});
	}

	const triggerAction = async () => {
		await triggerFn();
		if (closeOnButtonAction) {
			onClose(false);
		}
	};

	const footerButtons = (
		<>
			{!actionButtonActsAsCancel && <Button
				text={cancelButtonText}
				onClick={() => onCancel ? onCancel() : onClose(false)}
				type={BUTTON_TYPES.TERTIARY}
			/>}
			{footerDescription}
			{secondaryAction && <Button
				text={secondaryAction.text}
				onClick={secondaryAction.onClick}
				disabled={secondaryAction.disabled}
				loading={secondaryAction.loading}
				type={secondaryAction.type ?? BUTTON_TYPES.SECONDARY}
				size={actionButtonSize}
			/>}
			<Button
				text={actionButtonText}
				onClick={triggerAction}
				disabled={actionButtonDisabled}
				loading={actionButtonLoading}
				size={actionButtonSize}
			/>
		</>
	);

	return (
		<Modal
			open={open}
			toggleOpen={onClose}
			closeOnEscape={closeOnEscape}
			closeOnClickOutside={closeOnClickOutside}
		>
			<div data-testid={'@Ofload-Dialog'} className={`${styles.container} ${getClassName()}`}>
				<div className={styles.header}>
					<H2>{title}</H2>
					{text.length ? <Text text={text} /> : null}
				</div>
				{slot && <div className={styles.content}>{slot}</div>}
				<div className={`${styles.btns} ${(fullWidthFooter || footerLeft) && styles.btnsFullWidth}`}>
					{footerLeft}
					{footerLeft ? (
						<div className={styles.footerRightGroup}>{footerButtons}</div>
					) : footerButtons}
				</div>
			</div>
		</Modal>
	);
}

export const SmallDialog = (props: ISmallProps) => {
	return <Dialog
		{...props}
		size={'small'}
	/>
}

export const MediumDialog = (props: IMidLargeProps) => {
	return <Dialog
		{...props}
		size={'mid'}
	/>
}

export const LargeDialog = (props: IMidLargeProps) => {
	return <Dialog
		{...props}
		size={'large'}
	/>
}
